'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useGetConversationsQuery } from '@/api/features/conversations';
import { reduxLogger } from '@/core/utils/logger';
import { useTour } from '../hooks/useTour';
import { useCompleteTourWithTracking } from '../hooks/useCompleteTourWithTracking';
import { useTourAutoAdvance } from '../hooks/useTourAutoAdvance';
import { TourHighlight } from '../components/TourHighlight';
import { ContactManagementWelcomeModal } from './ContactManagementWelcomeModal';
import { ContactManagementCompleteModal } from './ContactManagementCompleteModal';
import { CONTACT_MANAGEMENT_TOUR_STEPS } from './types';

/** Count of highlight-type steps (for "Step X of Y" display) */
const HIGHLIGHT_STEPS = CONTACT_MANAGEMENT_TOUR_STEPS.filter((s) => s.type === 'highlight');

/**
 * ContactManagementTourOverlay - Renders the Contact Management tour step UI.
 *
 * Only renders when the active tour is 'contact-management'. Navigates the user
 * to the contacts page for the directory walkthrough, then to the Voley Support
 * conversation for the contact sidebar demonstration.
 */
export function ContactManagementTourOverlay() {
  const router = useRouter();
  const { isActive, activeTourId, currentStep, nextStep } = useTour();
  const { completeTourWithTracking } = useCompleteTourWithTracking();

  const isContactTour = activeTourId === 'contact-management';

  // Auto-advance polling
  useTourAutoAdvance(isContactTour, currentStep, nextStep);

  // Find the Voley Support conversation for sidebar demo
  const { data: searchResults } = useGetConversationsQuery(
    { search: 'Voley Support', limit: 1 },
    { skip: !isContactTour, refetchOnMountOrArgChange: 5 * 60 },
  );
  const voleySupportId = searchResults?.items?.[0]?.id;

  // Track which steps we've already navigated for (to avoid re-navigating)
  const navigatedForStepRef = useRef<string | null>(null);

  // Handle step-specific navigation
  useEffect(() => {
    if (!isContactTour || !currentStep) return;
    if (navigatedForStepRef.current === currentStep.id) return;

    // Steps that need the contacts page
    const contactsPageSteps = [
      'highlight_contacts_page',
      'highlight_contacts_search',
      'highlight_sync_btn',
      'highlight_bulk_message',
    ];
    if (contactsPageSteps.includes(currentStep.id)) {
      navigatedForStepRef.current = currentStep.id;
      router.push('/contacts');
      reduxLogger.info(`[ContactManagementTour] Navigating to contacts page for step: ${currentStep.id}`);
      return;
    }

    // Steps that need the Voley Support conversation with sidebar open
    const conversationSteps = [
      'highlight_contact_panel_btn',
      'highlight_contact_info',
      'highlight_consent',
      'highlight_tags',
      'highlight_notes',
      'highlight_manage_conversation_btn',
      'highlight_manage_conversation_expanded',
    ];
    if (conversationSteps.includes(currentStep.id) && voleySupportId) {
      navigatedForStepRef.current = currentStep.id;
      router.push(`/dashboard?view=all&conversation=${voleySupportId}`);
      reduxLogger.info(`[ContactManagementTour] Navigating to Voley Support for step: ${currentStep.id}`);
    }
  }, [isContactTour, currentStep?.id, currentStep, voleySupportId, router]);

  const handleWelcomeComplete = useCallback(() => {
    router.push('/contacts');
    reduxLogger.info('[ContactManagementTour] Navigating to contacts page');
    nextStep();
  }, [router, nextStep]);

  const handleFinish = useCallback(() => {
    completeTourWithTracking('contact-management');
    router.push('/dashboard?view=all');
  }, [completeTourWithTracking, router]);

  const handleHighlightNext = useCallback(() => {
    nextStep();
  }, [nextStep]);

  if (!isContactTour || !isActive || !currentStep) return null;

  // Backdrop visibility — block clicks only for non-interactive, non-auto-advancing highlight steps
  const isHighlightStep = currentStep.type === 'highlight';
  const isClickThrough = !!(
    currentStep.advanceOnNavigate ||
    currentStep.advanceOnElement ||
    currentStep.advanceOnElementRemoved
  );
  const isInteractive = !!currentStep.allowInteraction;
  const showBackdrop = isHighlightStep && !isClickThrough && !isInteractive;

  return (
    <>
      {/* Persistent backdrop */}
      <div
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 199,
          pointerEvents: showBackdrop ? 'auto' : 'none',
          opacity: showBackdrop ? 1 : 0,
        }}
      />

      {/* Step content */}
      {currentStep.type === 'modal' && (
        <>
          {currentStep.id === 'contacts_welcome' && <ContactManagementWelcomeModal onComplete={handleWelcomeComplete} />}
          {currentStep.id === 'contacts_complete' && <ContactManagementCompleteModal onFinish={handleFinish} />}
        </>
      )}

      {currentStep.type === 'highlight' &&
        (() => {
          const highlightIndex = HIGHLIGHT_STEPS.findIndex((s) => s.id === currentStep.id);
          return (
            <TourHighlight
              step={currentStep}
              stepNumber={highlightIndex + 1}
              totalSteps={HIGHLIGHT_STEPS.length}
              onNext={handleHighlightNext}
              isLastStep={highlightIndex === HIGHLIGHT_STEPS.length - 1}
            />
          );
        })()}
    </>
  );
}
